/**
 * Journey Progress Widget
 * Shows user's progress through the FinList interaction flow
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2, 
  Circle,
  Trophy,
  Sparkles
} from 'lucide-react';
import { 
  getUserJourney, 
  getNextStepSuggestion,
  JOURNEY_MILESTONES 
} from '@/services/journeyService';

export default function JourneyProgressWidget() {
  const [journey, setJourney] = useState(null);
  const [nextStep, setNextStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadJourney();
  }, []);

  async function loadJourney() {
    try {
      const [journeyData, suggestion] = await Promise.all([
        getUserJourney(),
        getNextStepSuggestion()
      ]);
      
      setJourney(journeyData);
      setNextStep(suggestion);
    } catch (error) {
      console.error('Error loading journey:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleNextStepClick() {
    if (nextStep?.route) {
      navigate(nextStep.route);
    }
  }

  if (loading || !journey) return null;

  // Hide if journey is complete
  if (journey.completionPercentage === 100) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-6 shadow-lg"
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">
              🎊 Journey Complete!
            </h3>
            <p className="text-white/90 text-sm">
              You're now a FinList pro! Keep managing your finances like a boss.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden"
    >
      {/* Compact View */}
      <div className="p-4">
        <div className="flex items-center gap-4 mb-3">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-gray-900 dark:text-white">Your FinList Journey</h3>
              <span className="text-sm font-semibold text-teal-600">
                {journey.completionPercentage}%
              </span>
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${journey.completionPercentage}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-teal-500 to-green-500 rounded-full"
              />
            </div>
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="w-10 h-10 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center justify-center transition-colors"
          >
            <Sparkles className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Next Step CTA */}
        {nextStep && (
          <button
            onClick={handleNextStepClick}
            className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-teal-500 to-green-500 text-white font-semibold flex items-center justify-between hover:shadow-lg transition-all"
          >
            <span className="flex items-center gap-2">
              <span className="text-2xl">{nextStep.title.split(' ')[0]}</span>
              <span>{nextStep.action}</span>
            </span>
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Expanded View */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="border-t border-gray-100 dark:border-slate-700 p-4 bg-gray-50 dark:bg-slate-700"
        >
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Journey Milestones</h4>
          
          <div className="space-y-2">
            {JOURNEY_MILESTONES.filter(m => m.id !== 'completed').map((milestone) => {
              const isCompleted = journey.completedSteps?.includes(milestone.id);
              const isCurrent = journey.currentStep === milestone.id;

              return (
                <div
                  key={milestone.id}
                  className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
                    isCurrent ? 'bg-teal-50 border-2 border-teal-200' : 'bg-white dark:bg-slate-800'
                  }`}
                >
                  <div className="mt-0.5">
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <Circle className={`w-5 h-5 ${isCurrent ? 'text-teal-500' : 'text-gray-300 dark:text-gray-600'}`} />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${isCompleted ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                      {milestone.title}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={() => setExpanded(false)}
            className="w-full mt-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium"
          >
            Show Less
          </button>
        </motion.div>
      )}
    </motion.div>
  );
}
